import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { format, subDays, differenceInCalendarDays, isSameDay } from 'date-fns'
import { useForm } from 'react-hook-form'
import {
  Box, Button, Chip, Grid, InputAdornment, LinearProgress, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import {
  PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip as ReTooltip, ReferenceLine,
} from 'recharts'
import SearchIcon from '@mui/icons-material/Search'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import BoltIcon from '@mui/icons-material/Bolt'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import BarChartIcon from '@mui/icons-material/BarChart'
import { apiConstants } from '../../common/constants'
import { formatNumber } from '../../common/util'
import PageTitle from '../../components/PageTitle'
import DateRangePicker from '../../components/DateRangePicker'
import Loader from '../../common/Loader'
import { FormProvider } from '../../common/hook-form'
import { MerchantFilterCached } from '../../common/ProshipFormComponents'

// ─────────────────────────────────────────────────────────────────────────────
// Daily Allocation dashboard (TASK-008). Reads the frozen MySQL rollup via
// GET /api/allocation/dashboard/summary — every number is a snapshot finalized
// at 02:30 IST; the page never shows "today". Definition matches the D2C
// Overall Shipments dashboard (B2C, awbRegisteredDate, cancelled excluded).
// ─────────────────────────────────────────────────────────────────────────────

const P1_TARGET_PCT = 80
const MAX_RANGE_DAYS = 30
// Analytics history before this was purged (TASK-008 §3); server enforces too.
const MIN_DATE = new Date(2026, 5, 24) // 24 Jun 2026

const COLORS = {
  auto: '#2e7d32', manual: '#e65100',
  p1: '#1565c0', p2: '#2e7d32', p3plus: '#8e24aa', notRecorded: '#9e9e9e',
  good: '#2e7d32', bad: '#c62828', warn: '#e65100',
}

const SHORTCUTS = [
  { key: 'today', label: 'Today (Live)', days: 0 },
  { key: 'yesterday', label: 'Yesterday', days: 1 },
  { key: 'last3', label: 'Last 3 days', days: 3 },
  { key: 'last7', label: 'Last 7 days', days: 7 },
  { key: 'last15', label: 'Last 15 days', days: 15 },
]

// days=0 -> today (live, hourly-refreshed); otherwise a window ending yesterday.
function rangeForShortcut(days) {
  if (days === 0) {
    const today = new Date()
    return { start: today, end: today }
  }
  const end = subDays(new Date(), 1)
  return { start: subDays(end, days - 1), end }
}

// Column order for the RCA CSV — must match AllocationRcaService.COLUMNS.
const RCA_COLUMNS = [
  'Order Reference', 'Order Date', 'Merchant Name', 'Zone', 'Weight (g)',
  'Pickup Pincode', 'Drop Pincode', 'Invoice Value', 'Allocation Conditions',
  'Attempts', 'Allocated Priority', 'Priority Bucket', 'Allocated Partner', 'AWB',
  'P1 Partner', 'P1 Failure Reason', 'P1 Sub Reason',
  'P2 Partner', 'P2 Failure Reason',
  'Primary RCA', 'Failure Chain', 'Downgrade Levels',
]

const fmtPct = (num, den) => (den > 0 ? ((num / den) * 100).toFixed(2) + '%' : '—')

// ── KPI Card (same dialect as OrderAnalytics/Analytics.js) ──────────────────
function KpiCard({ icon, label, value, sub, color, bg, chip, info }) {
  return (
    <Box sx={{
      borderRadius: 2.5, border: '1px solid #e8e8e8', backgroundColor: '#fff',
      p: 2, height: '100%', display: 'flex', gap: 1.5, alignItems: 'flex-start',
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: 2, backgroundColor: bg, border: `1px solid ${color}25`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>
            {label}
          </Typography>
          {info && (
            <Tooltip arrow placement='top' title={<Typography sx={{ fontSize: '0.7rem', lineHeight: 1.5 }}>{info}</Typography>}>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: '#bdbdbd', cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Box>
        <Typography component='div' sx={{ fontWeight: 800, fontSize: '1.55rem', color: '#1a1a1a', lineHeight: 1.15, mt: 0.4 }}>
          {value} {chip}
        </Typography>
        {sub && <Typography sx={{ color: '#9e9e9e', fontSize: '0.7rem', mt: 0.3 }}>{sub}</Typography>}
      </Box>
    </Box>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────
function OrderAllocationAnalytics() {
  const [shortcut, setShortcut] = useState('yesterday')
  const [range, setRange] = useState(() => {
    const r = rangeForShortcut(1)
    return [{ startDate: r.start, endDate: r.end, key: 'selection' }]
  })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [rca, setRca] = useState(null)
  const [rcaLoading, setRcaLoading] = useState(false)
  const [rcaDownloading, setRcaDownloading] = useState(false)

  // MerchantFilterCached is RHF-based — a tiny form context just for it.
  const methods = useForm({ defaultValues: { merchant: '' } })
  const merchantOid = methods.watch('merchant')

  const fromDate = range[0]?.startDate
  const toDate = range[0]?.endDate

  useEffect(() => {
    if (!fromDate || !toDate) return
    if (differenceInCalendarDays(toDate, fromDate) + 1 > MAX_RANGE_DAYS) {
      setError(`Maximum date range is ${MAX_RANGE_DAYS} days`)
      setData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    axios.get(`${apiConstants.BASE_LOCAL}${apiConstants.ALLOCATION_DASHBOARD_SUMMARY}`, {
      params: {
        fromDate: format(fromDate, 'yyyy-MM-dd'),
        toDate: format(toDate, 'yyyy-MM-dd'),
        ...(merchantOid ? { merchantOid } : {}),
      },
    })
      .then((res) => { if (!cancelled) setData(res.data) })
      .catch((err) => {
        if (!cancelled) {
          setData(null)
          setError(err?.response?.data?.message || err.message || 'Failed to load allocation summary')
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [fromDate, toDate, merchantOid])

  // Derived selection facts (used by the RCA section + live chip).
  const isSingleDay = fromDate && toDate && isSameDay(fromDate, toDate)
  const isTodaySelected = isSingleDay && isSameDay(toDate, new Date())

  // RCA summary — single-day selections only (range RCA goes through Reports).
  useEffect(() => {
    if (!isSingleDay) { setRca(null); return }
    let cancelled = false
    setRcaLoading(true)
    axios.get(`${apiConstants.BASE_LOCAL}${apiConstants.ALLOCATION_DASHBOARD_RCA_SUMMARY}`, {
      params: { date: format(toDate, 'yyyy-MM-dd'), ...(merchantOid ? { merchantOid } : {}) },
    })
      .then((res) => { if (!cancelled) setRca(res.data) })
      .catch(() => { if (!cancelled) setRca(null) })
      .finally(() => { if (!cancelled) setRcaLoading(false) })
    return () => { cancelled = true }
  }, [isSingleDay, fromDate, toDate, merchantOid])

  // Same-day CSV download (dashboard exports TODAY only; ranges/past via Reports).
  // CSV, not xlsx, so the file format is byte-identical to the Reports-section
  // export — both come from the same backend row engine and the same columns.
  const downloadRcaCsv = () => {
    setRcaDownloading(true)
    axios.get(`${apiConstants.BASE_LOCAL}${apiConstants.ALLOCATION_DASHBOARD_RCA_ROWS}`, {
      params: { date: format(toDate, 'yyyy-MM-dd'), ...(merchantOid ? { merchantOid } : {}) },
    })
      .then((res) => {
        const rows = res.data || []
        const esc = (v) => {
          let s = v === null || v === undefined ? '' : String(v)
          // Formula-injection guard: courier error strings are third-party data.
          if (/^[=+\-@]/.test(s)) s = "'" + s
          return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
        }
        const csv = [RCA_COLUMNS.map(esc).join(','),
          ...rows.map((r) => RCA_COLUMNS.map((c) => esc(r[c])).join(','))].join('\n')
        // BOM so Excel opens UTF-8 correctly.
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = `Allocation_P2P3_RCA_${format(toDate, 'yyyy-MM-dd')}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(a.href)
      })
      .finally(() => setRcaDownloading(false))
  }

  const applyShortcut = (sc) => {
    setShortcut(sc.key)
    const r = rangeForShortcut(sc.days)
    setRange([{ startDate: r.start, endDate: r.end, key: 'selection' }])
  }

  const onPickerChange = (r) => {
    if (!r?.length || !r[0].startDate || !r[0].endDate) return
    setShortcut(null)
    setRange(r)
  }

  const totals = data?.totals
  const days = data?.days || []
  const merchants = data?.merchants || []

  const p1SharePct = totals && totals.auto > 0 ? (totals.p1 / totals.auto) * 100 : null
  const isOptimal = p1SharePct !== null && p1SharePct >= P1_TARGET_PCT

  const donutData = useMemo(() => (totals ? [
    { name: 'Auto allocated', value: totals.auto, color: COLORS.auto },
    { name: 'Manual allocation', value: totals.manual, color: COLORS.manual },
  ].filter((d) => d.value > 0) : []), [totals])

  const trendData = useMemo(() => days.map((d) => ({
    date: format(new Date(d.date), 'dd MMM'),
    p1Share: d.auto > 0 ? Math.round((d.p1 / d.auto) * 10000) / 100 : null,
  })), [days])

  const priorityRows = totals ? [
    { key: 'p1', label: 'P1', desc: 'Booked with first preference courier', count: totals.p1, color: COLORS.p1 },
    { key: 'p2', label: 'P2', desc: 'Booked with second preference courier', count: totals.p2, color: COLORS.p2 },
    { key: 'p3plus', label: 'P3+', desc: 'Booked with third preference or later', count: totals.p3plus, color: COLORS.p3plus },
    { key: 'nr', label: 'N/R', desc: 'Priority not recorded', count: totals.pNotRecorded, color: COLORS.notRecorded },
  ] : []

  const filteredMerchants = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return merchants
    return merchants.filter((m) => (m.merchantName || m.merchantOid || '').toLowerCase().includes(q))
  }, [merchants, search])

  const rangeLabel = fromDate && toDate
    ? (format(fromDate, 'dd MMM yyyy') === format(toDate, 'dd MMM yyyy')
      ? format(fromDate, 'dd MMM yyyy')
      : `${format(fromDate, 'dd MMM yyyy')} – ${format(toDate, 'dd MMM yyyy')}`)
    : ''

  const hasData = !loading && !error && totals && totals.total > 0

  return (
    <>
      <PageTitle>Shipment Allocation Analytics</PageTitle>

      <Grid container spacing={2}>
        {/* ── Header strip: shortcuts + range + merchant ── */}
        <Grid item xs={12}>
          <Box sx={{
            backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5,
            p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{ display: 'flex', border: '1px solid #dfe3e8', borderRadius: 2, overflow: 'hidden' }}>
              {SHORTCUTS.map((sc) => (
                <Button
                  key={sc.key}
                  onClick={() => applyShortcut(sc)}
                  sx={{
                    borderRadius: 0, textTransform: 'none', fontSize: '0.76rem', px: 1.8, py: 0.9,
                    fontWeight: shortcut === sc.key ? 700 : 600,
                    color: shortcut === sc.key ? '#fff' : '#637381',
                    backgroundColor: shortcut === sc.key ? '#1565c0' : '#fff',
                    borderRight: '1px solid #dfe3e8', '&:last-child': { borderRight: 'none' },
                    '&:hover': { backgroundColor: shortcut === sc.key ? '#0d47a1' : '#fafafa' },
                  }}
                >
                  {sc.label}
                </Button>
              ))}
            </Box>

            <DateRangePicker
              defaultRange={range}
              onChange={onPickerChange}
              minDate={MIN_DATE}
              maxDate={subDays(new Date(), 1)}
            />

            <FormProvider methods={methods} onSubmit={(e) => e?.preventDefault?.()}>
              <Box sx={{ width: 250 }}>
                <MerchantFilterCached fullWidth placeholder='All merchants' />
              </Box>
            </FormProvider>

            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: '0.68rem', color: '#9e9e9e' }}>
              Data from 24 Jun 2026 · today live via shortcut · ranges up to yesterday, max {MAX_RANGE_DAYS} days
            </Typography>
            {data?.includesToday ? (
              <Chip
                size='small'
                label={`Live · updated ${data?.lastUpdatedAt ? format(new Date(data.lastUpdatedAt), 'HH:mm') : '—'} IST · refreshes hourly`}
                sx={{ height: 24, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#e8f5e9', color: '#2e7d32' }}
              />
            ) : (
              <Chip
                size='small'
                label='Frozen snapshot · finalized daily 02:30 IST'
                sx={{ height: 24, fontSize: '0.68rem', fontWeight: 600, backgroundColor: '#e3f2fd', color: '#1565c0' }}
              />
            )}
          </Box>
        </Grid>

        {loading && <Grid item xs={12}><Loader /></Grid>}

        {/* ── Error / empty states ── */}
        {!loading && error && (
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 5, textAlign: 'center' }}>
              <BarChartIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#757575', mb: 0.5 }}>
                Could not load allocation data
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e', maxWidth: 460, mx: 'auto' }}>{error}</Typography>
            </Box>
          </Grid>
        )}
        {!loading && !error && totals && totals.total === 0 && (
          <Grid item xs={12}>
            <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 5, textAlign: 'center' }}>
              <BarChartIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#757575', mb: 0.5 }}>
                No shipments for {rangeLabel}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#9e9e9e' }}>
                The rollup may not have run for this range yet, or the selected merchant had no bookings.
              </Typography>
            </Box>
          </Grid>
        )}

        {/* ── KPI cards ── */}
        {hasData && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<LocalShippingIcon sx={{ fontSize: 22, color: '#1565c0' }} />}
                label='Total Shipments Booked'
                value={formatNumber(totals.total)}
                sub={`AWB registered · ${rangeLabel}`}
                color='#1565c0' bg='#e3f2fd'
                info='All B2C AWBs registered in the range, cancelled excluded — same definition as the D2C Overall Shipments dashboard.'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<BoltIcon sx={{ fontSize: 22, color: COLORS.auto }} />}
                label='Auto Allocated'
                value={formatNumber(totals.auto)}
                sub={`${fmtPct(totals.auto, totals.total)} of total · engine picked courier`}
                color={COLORS.auto} bg='#e8f5e9'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<TrackChangesIcon sx={{ fontSize: 22, color: isOptimal ? COLORS.good : COLORS.bad }} />}
                label={`P1 Share · Target ${P1_TARGET_PCT}%`}
                value={
                  <Box component='span' sx={{ color: isOptimal ? COLORS.good : COLORS.bad }}>
                    {p1SharePct !== null ? p1SharePct.toFixed(2) + '%' : '—'}
                  </Box>
                }
                chip={
                  <Chip
                    size='small'
                    label={isOptimal ? 'Optimal' : 'Non Optimal'}
                    sx={{
                      height: 20, fontSize: '0.64rem', fontWeight: 700, verticalAlign: 'middle',
                      backgroundColor: isOptimal ? '#e8f5e9' : '#fdecea',
                      color: isOptimal ? COLORS.good : COLORS.bad,
                    }}
                  />
                }
                sub={`${formatNumber(totals.p1)} of ${formatNumber(totals.auto)} auto AWBs`}
                color={isOptimal ? COLORS.good : COLORS.bad} bg={isOptimal ? '#e8f5e9' : '#fdecea'}
                info='Share of auto-allocated shipments booked on the first-preference courier. A low share is a serviceability signal, not a booking defect.'
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                icon={<PersonOutlineIcon sx={{ fontSize: 22, color: COLORS.manual }} />}
                label='Manual Allocation'
                value={formatNumber(totals.manual)}
                sub={`${fmtPct(totals.manual, totals.total)} of total · courier was pre-selected`}
                color={COLORS.manual} bg='#fff3e0'
                info='Courier was supplied with the order (by Ops or the merchant channel), so there is no courier preference to measure.'
              />
            </Grid>

            {/* ── Reconcile strip ── */}
            <Grid item xs={12}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                backgroundColor: '#e8f5e9', border: '1px solid #cde9d0', borderRadius: 2, px: 2, py: 1,
              }}>
                <CheckCircleIcon sx={{ fontSize: 17, color: COLORS.good }} />
                <Typography sx={{ fontSize: '0.76rem', color: '#1e5a26' }}>
                  Reconciled — <b>{formatNumber(totals.auto)} auto</b> + <b>{formatNumber(totals.manual)} manual</b> = <b>{formatNumber(totals.total)} total booked</b>.
                  Merchant rows below sum to the same total. ({formatNumber(totals.cancelledExcluded)} cancelled excluded, matching the Overall Shipments dashboard.)
                </Typography>
              </Box>
            </Grid>

            {/* ── Charts row ── */}
            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 2, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Booking Mode Mix</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', mb: 1 }}>How each AWB got its courier</Typography>
                <Box sx={{ position: 'relative', height: 190 }}>
                  <ResponsiveContainer width='100%' height='100%'>
                    <PieChart>
                      <Pie data={donutData} dataKey='value' nameKey='name' innerRadius={55} outerRadius={80} paddingAngle={2} isAnimationActive={false}>
                        {donutData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Pie>
                      <ReTooltip formatter={(v, n) => [formatNumber(v), n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                  }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>{formatNumber(totals.total)}</Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.5 }}>Booked</Typography>
                  </Box>
                </Box>
                {donutData.map((d) => (
                  <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.7 }}>
                    <Box sx={{ width: 11, height: 11, borderRadius: '3.5px', backgroundColor: d.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.76rem', color: '#637381', flex: 1 }}>{d.name}</Typography>
                    <Typography sx={{ fontSize: '0.76rem', fontWeight: 700 }}>{formatNumber(d.value)}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', width: 56, textAlign: 'right' }}>{fmtPct(d.value, totals.total)}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 2, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Courier Priority</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e' }}>
                  Auto allocated base only · {formatNumber(totals.auto)} AWBs
                </Typography>
                {/* stacked bar with target tick */}
                <Box sx={{ position: 'relative', display: 'flex', height: 30, mt: 3.5, mb: 1 }}>
                  {priorityRows.filter((p) => p.count > 0).map((p, i, arr) => (
                    <Tooltip key={p.key} arrow title={`${p.desc}: ${formatNumber(p.count)} (${fmtPct(p.count, totals.auto)})`}>
                      <Box sx={{
                        width: `${(p.count / Math.max(totals.auto, 1)) * 100}%`,
                        backgroundColor: p.color, cursor: 'pointer',
                        borderRadius: i === 0 ? '8px 0 0 8px' : i === arr.length - 1 ? '0 8px 8px 0' : 0,
                        ml: i === 0 ? 0 : '2px',
                      }} />
                    </Tooltip>
                  ))}
                  <Box sx={{ position: 'absolute', left: `${P1_TARGET_PCT}%`, top: -8, bottom: -8, borderLeft: '2px dashed #c62828' }}>
                    <Typography sx={{ position: 'absolute', top: -15, left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', fontWeight: 700, color: '#c62828', whiteSpace: 'nowrap' }}>
                      {P1_TARGET_PCT}% target
                    </Typography>
                  </Box>
                </Box>
                {priorityRows.map((p) => (
                  <Box key={p.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip label={p.label} size='small' sx={{
                      height: 22, minWidth: 42, fontWeight: 700, fontSize: '0.68rem',
                      backgroundColor: `${p.color}18`, color: p.color,
                    }} />
                    <Typography sx={{ fontSize: '0.76rem', color: '#637381', flex: 1 }}>{p.desc}</Typography>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>{formatNumber(p.count)}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', width: 56, textAlign: 'right' }}>{fmtPct(p.count, totals.auto)}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 2, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>P1 Share Trend</Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', mb: 1 }}>
                  Daily P1 share of auto allocated base
                </Typography>
                {trendData.length > 1 ? (
                  <ResponsiveContainer width='100%' height={230}>
                    <LineChart data={trendData} margin={{ top: 12, right: 12, bottom: 0, left: -18 }}>
                      <CartesianGrid strokeDasharray='0' stroke='#f0f0f0' vertical={false} />
                      <XAxis dataKey='date' tick={{ fontSize: 10, fill: '#9e9e9e' }} tickLine={false} axisLine={{ stroke: '#e0e0e0' }} />
                      <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: '#9e9e9e' }} tickLine={false} axisLine={false} unit='%' />
                      <ReTooltip formatter={(v) => [`${v}%`, 'P1 share']} />
                      <ReferenceLine y={P1_TARGET_PCT} stroke='#c62828' strokeDasharray='5 4' label={{ value: `${P1_TARGET_PCT}% target`, position: 'insideTopRight', fontSize: 10, fill: '#c62828' }} />
                      <Line type='monotone' dataKey='p1Share' stroke='#1565c0' strokeWidth={2.4} dot={{ r: 2.5 }} activeDot={{ r: 4.5 }} connectNulls isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: 230, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '0.78rem', color: '#9e9e9e', textAlign: 'center' }}>
                      Single-day view — pick a longer range<br />to see the trend.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* ── Merchant table ── */}
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                  <Box sx={{ flex: 1, minWidth: 220 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Merchant Wise Detail</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e' }}>
                      Counts reconcile to the range total · P1/P2/P3+ measured on each merchant&apos;s auto allocated AWBs
                    </Typography>
                  </Box>
                  <Chip label='Sorted by lowest P1 share' size='small'
                        sx={{ height: 22, fontSize: '0.66rem', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0' }} />
                  <TextField
                    size='small' placeholder='Search merchant…' value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position='start'><SearchIcon sx={{ fontSize: 16, color: '#9e9e9e' }} /></InputAdornment>,
                      sx: { fontSize: '0.78rem', borderRadius: 2 },
                    }}
                    sx={{ width: 210 }}
                  />
                </Box>

                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table stickyHeader size='small' sx={{ minWidth: 880 }}>
                    <TableHead>
                      <TableRow>
                        {['Merchant', 'Total', 'Auto', 'Manual', 'P1 (of auto)', 'P2 (of auto)', 'P3+ (of auto)', 'Priority Not Recorded', 'Status'].map((h, i) => (
                          <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{
                            fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: 0.5,
                            color: '#637381', backgroundColor: '#fafbfc', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap',
                          }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMerchants.map((m) => {
                        const p1Pct = m.auto > 0 ? (m.p1 / m.auto) * 100 : null
                        const optimal = p1Pct !== null && p1Pct >= P1_TARGET_PCT
                        const p1Color = p1Pct === null ? '#9e9e9e' : p1Pct >= P1_TARGET_PCT ? COLORS.good : p1Pct >= 60 ? COLORS.warn : COLORS.bad
                        return (
                          <TableRow key={m.merchantOid} hover>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{m.merchantName || m.merchantOid}</TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem' }}>{formatNumber(m.total)}</TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem' }}>{formatNumber(m.auto)}</TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem' }}>{formatNumber(m.manual)}</TableCell>
                            <TableCell align='right' sx={{ minWidth: 120 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: p1Color }}>{formatNumber(m.p1)}</Typography>
                              <Typography sx={{ fontSize: '0.66rem', color: '#9e9e9e' }}>{p1Pct !== null ? p1Pct.toFixed(2) + '%' : '—'}</Typography>
                              <LinearProgress
                                variant='determinate' value={Math.min(p1Pct || 0, 100)}
                                sx={{
                                  height: 3.5, borderRadius: 2, backgroundColor: '#f0f0f0', mt: 0.4,
                                  '& .MuiLinearProgress-bar': { borderRadius: 2, backgroundColor: p1Color },
                                }}
                              />
                            </TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem' }}>
                              {formatNumber(m.p2)}
                              <Typography sx={{ fontSize: '0.66rem', color: '#9e9e9e' }}>{fmtPct(m.p2, m.auto)}</Typography>
                            </TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem' }}>
                              {formatNumber(m.p3plus)}
                              <Typography sx={{ fontSize: '0.66rem', color: '#9e9e9e' }}>{fmtPct(m.p3plus, m.auto)}</Typography>
                            </TableCell>
                            <TableCell align='right' sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>{formatNumber(m.pNotRecorded)}</TableCell>
                            <TableCell align='right'>
                              <Chip
                                size='small'
                                label={p1Pct === null ? 'No auto' : optimal ? 'Optimal' : 'Non Optimal'}
                                sx={{
                                  height: 20, fontSize: '0.64rem', fontWeight: 700,
                                  backgroundColor: p1Pct === null ? '#fafafa' : optimal ? '#e8f5e9' : '#fdecea',
                                  color: p1Pct === null ? '#9e9e9e' : optimal ? COLORS.good : COLORS.bad,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Grid>

            {/* ── P2/P3 RCA section (single-day selections) ── */}
            {isSingleDay && (
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>P2/P3 Allocation RCA</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e' }}>
                        Why shipments were booked below first preference · {format(toDate, 'dd MMM yyyy')}
                        {rca ? ` · ${formatNumber(rca.totalP2P3 || 0)} shipments` : ''}
                        {isTodaySelected && (
                          <Box component='span' sx={{
                            ml: 0.8, px: 0.9, py: 0.2, borderRadius: 10, fontSize: '0.62rem', fontWeight: 700,
                            backgroundColor: '#e8f5e9', color: '#2e7d32', verticalAlign: 'middle',
                          }}>
                            LIVE — real-time; may run ahead of the hourly cards above
                          </Box>
                        )}
                      </Typography>
                    </Box>
                    {isTodaySelected ? (
                      <Button
                        variant='contained' size='small' disabled={rcaDownloading || !rca || !(rca.totalP2P3 > 0)}
                        onClick={downloadRcaCsv}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' } }}
                      >
                        {rcaDownloading ? 'Preparing…' : 'Download CSV (today)'}
                      </Button>
                    ) : (
                      <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e' }}>
                        Past-date &amp; range extracts: Reports → Create Report → &quot;Allocation P2/P3 RCA report&quot;
                      </Typography>
                    )}
                  </Box>
                  {rcaLoading && <Box sx={{ p: 3 }}><Loader /></Box>}
                  {!rcaLoading && rca && (rca.merchants || []).length === 0 && (
                    <Typography sx={{ p: 3, fontSize: '0.8rem', color: '#9e9e9e', textAlign: 'center' }}>
                      No P2/P3 bookings for this selection.
                    </Typography>
                  )}
                  {!rcaLoading && rca && (rca.merchants || []).length > 0 && (
                    <TableContainer sx={{ maxHeight: 420 }}>
                      <Table stickyHeader size='small' sx={{ minWidth: 720 }}>
                        <TableHead>
                          <TableRow>
                            {['Merchant', 'P2/P3 Shipments', 'Serviceability Miss', 'Courier Booking Failure', 'Reason Not Recorded'].map((h, i) => (
                              <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{
                                fontWeight: 700, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: 0.5,
                                color: '#637381', backgroundColor: '#fafbfc', borderBottom: '2px solid #e0e0e0', whiteSpace: 'nowrap',
                              }}>
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rca.merchants.map((m) => {
                            const reasons = m.reasons || {}
                            const svc = reasons['Serviceability - Proship master (NOT_SERVICEABLE)'] || 0
                            const api = reasons['Booking failure - Courier API'] || 0
                            const nr = reasons['Not recorded'] || 0
                            return (
                              <TableRow key={m.merchantName} hover>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{m.merchantName}</TableCell>
                                <TableCell align='right' sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{formatNumber(m.p2p3Total)}</TableCell>
                                <TableCell align='right' sx={{ fontSize: '0.8rem' }}>{formatNumber(svc)}</TableCell>
                                <TableCell align='right' sx={{ fontSize: '0.8rem' }}>{formatNumber(api)}</TableCell>
                                <TableCell align='right' sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>{formatNumber(nr)}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Grid>
            )}

            {/* ── How to read ── */}
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, p: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>How to read this dashboard</Typography>
                <Typography component='ul' sx={{ m: 0, pl: 2.2, '& li': { fontSize: '0.75rem', color: '#637381', mb: 0.5 } }}>
                  <li><b>Auto allocated</b> means the ProShip engine picked the courier from the merchant&apos;s priority list. Courier priority (P1/P2/P3+) is measurable only on these AWBs.</li>
                  <li><b>Manual allocation</b> means the courier was pre-selected (by Ops or the merchant&apos;s channel), so there is no first or second choice to measure. Reported, not excluded.</li>
                  <li>A <b>low P1 share is a serviceability signal</b>, not a booking defect — it means the P1 courier did not cover the destination PIN code.</li>
                  <li><b>Today (Live)</b> refreshes hourly and converges through the day — async couriers (Shadowfax/Ekart) show as &quot;priority pending&quot; for a few minutes until their AWB confirms. Past days are <b>finalized at 02:30 IST</b> and never change — the daily email reads the same data. Coverage: <b>24 Jun 2026 onwards</b>.</li>
                </Typography>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default OrderAllocationAnalytics
